import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * The small Markdown subset the assistant writes (docs/17 §Stil): paragraphs, bullet and numbered lists, **bold**,
 * `code` and links. Built as React elements, never as HTML (no injection). App links (/objekte/…) use the router;
 * other links only https, opened in a new tab.
 */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function inline(text: string, key: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const k = `${key}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={k}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part)) return <code key={k}>{part.slice(1, -1)}</code>;
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      const [, label, href] = link as unknown as [string, string, string];
      if (/^\/(?!\/)/.test(href)) return <Link key={k} to={href}>{label}</Link>;
      if (/^https:\/\//.test(href)) return <a key={k} href={href} target="_blank" rel="noopener noreferrer">{label}</a>;
      return <Fragment key={k}>{label}</Fragment>;
    }
    return <Fragment key={k}>{part}</Fragment>;
  });
}

export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r/g, '').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const bullet = /^\s*[-*•]\s+/;
    const numbered = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? numbered : bullet).test(lines[i]!)) items.push(lines[i++]!.replace(ordered ? numbered : bullet, ''));
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(<Tag key={`b${i}`}>{items.map((it, n) => <li key={n}>{inline(it, `l${i}-${n}`)}</li>)}</Tag>);
      continue;
    }
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(<p key={`h${i}`} className="agent-md-head">{inline(heading[1]!, `h${i}`)}</p>);
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !bullet.test(lines[i]!) && !numbered.test(lines[i]!) && !/^#{1,4}\s/.test(lines[i]!)) para.push(lines[i++]!);
    blocks.push(<p key={`p${i}`}>{para.flatMap((l, n) => (n ? [<br key={`br${n}`} />, ...inline(l, `p${i}-${n}`)] : inline(l, `p${i}-${n}`)))}</p>);
  }
  return <div className="agent-md">{blocks}</div>;
}
